import type { GradePublic } from '@repo/types/grades';

import { AccountType } from '@repo/types/enums/accountType';
import { UserLvl } from '@repo/types/enums/userLvl';

import { useCredentials } from '@/store/auth-store';

export interface GradeOriginLink {
  name: string;
  to: string;
}

/**
 * The quiz or assignment a grade was produced from, as a name and a route. The
 * backend resolves the item onto the grade, so this only picks the route for the
 * caller's role: teachers and admins get `/manage` routes, students the
 * student-facing ones. Null for a manual grade, when the item was deleted, or
 * for a parent — every destination fetches the viewer's own attempt.
 */
export function useGradeOriginLink(grade: GradePublic): GradeOriginLink | null {
  const { lvl, accountType } = useCredentials();
  const isParentAccount = accountType === AccountType.Parent;
  const isStaff = !isParentAccount && lvl !== UserLvl.STUDENT;

  if (isParentAccount) return null;

  switch (grade.originType) {
    case 'quizResponse':
      return grade.quiz
        ? {
            name: grade.quiz.name,
            to: isStaff ? `/manage/quizzes/${grade.quiz.id}` : `/quizzes/${grade.quiz.id}`,
          }
        : null;
    case 'assignmentResponse':
      return grade.assignment
        ? {
            name: grade.assignment.name,
            to: isStaff ? `/manage/assignments/${grade.assignment.id}` : `/assignments/${grade.assignment.id}`,
          }
        : null;
    case 'other':
      return null;
  }
}
